import { css, cva } from '@/styled-system/css';

// Expands the group to fill its container and distributes buttons equally.
// Mirrors ButtonGroup's `fluid` prop which ToggleButtonGroup doesn't support.
// When the container is too narrow for every button's natural padding (e.g.
// the mobile filter modal while the keyboard shrinks the viewport), buttons
// hold their min-content width and the group scrolls horizontally instead of
// squeezing padding or wrapping labels. The scroll container also zeroes the
// group's automatic minimum size as a flex item, so it can't widen the filter
// modal past narrow viewports (<360px).
export const rangeToggleGroupFluidStyles = css.raw({
  width: 'calc(100% + {spacing.default})',
  overflowX: 'auto',
  padding: 'tighter',
  margin: 'calc({spacing.tighter} * -1)',
  '& > button': {
    // grow from a 0% basis → equal-width buttons while space allows; the
    // min-content floor (no shrink, no wrap) kicks in below that and
    // overflows into the group's own horizontal scroll.
    flex: '1 0 0%',
    whiteSpace: 'nowrap',
  },
});

// Cover the vertical seam between consecutive selected buttons.
// Constellation's ToggleButton uses `box-shadow: inset 0 0 0 2px` for its
// selected ring. Adjacent selected buttons each emit a 2px shadow at the shared
// vertical edge, creating a doubled line. The ::before pseudo spans 4px wide
// at the seam (2px into the preceding button + 2px of this button's left shadow)
// and fills with the selected background token, making the range read as one
// continuous block. Inset 2px from top/bottom so the horizontal ring strips
// (top/bottom of each button) remain visible and connect seamlessly.
export const rangeToggleGroupRecipe = cva({
  base: {
    '& [aria-pressed="true"] + [aria-pressed="true"]::before': {
      content: '""',
      position: 'absolute',
      // eslint-disable-next-line hotpads/no-magic-numbers -- 2px = Constellation's borderWidths.selected ring height; leave the top/bottom ring strips uncovered so they connect as one continuous border
      insetBlockStart: '2px',
      // eslint-disable-next-line hotpads/no-magic-numbers -- matches borderWidths.selected
      insetBlockEnd: '2px',
      // eslint-disable-next-line hotpads/no-magic-numbers -- extends 2px into the preceding button to cover its right shadow
      insetInlineStart: '-2px',
      // eslint-disable-next-line hotpads/no-magic-numbers -- 2px preceding button right shadow + 2px this button left shadow
      width: '4px',
      backgroundColor: 'bg.action.soft.selected.default',
      zIndex: 1,
    },
  },
  variants: {
    fluid: {
      true: rangeToggleGroupFluidStyles,
    },
  },
});
